import React from 'react';
import { motion } from 'framer-motion';
import './CalculationLibrary.css';

const CalculationLibrary = () => {
  const heroStats = [
    { label: '覆盖收费标准', value: '40+ 项' },
    { label: '更新频率', value: '每月' },
    { label: '行业用户', value: '10,000+' },
    { label: '支持场景', value: '设计 / 施工 / 监理' }
  ];

  const calculationTools = [
    { id: 1, name: '招投标费', description: '依据国家发改委标准，快速计算不同规模项目的招投标费用。' },
    { id: 2, name: '社会中介机构审计费', description: '根据审计范围与投资额生成权威报价参考。' },
    { id: 3, name: '设计费', description: '按复杂程度、阶段系数测算设计费用，支持多专业。' },
    { id: 4, name: '监理费', description: '依据住建部标准计算监理服务费，自动匹配专业类别。' },
    { id: 5, name: '项目建设管理费', description: '覆盖管理人员、办公通信、差旅等成本项。' },
    { id: 6, name: '代建管理费', description: '适用于政府投资项目的代建费用测算。' },
    { id: 7, name: '环境影响评价费', description: '依据环评收费标准与项目等级估算费用。' },
    { id: 8, name: '水土保持费', description: '支持不同行业的水保方案费用测算。' },
    { id: 9, name: '建设期利息', description: '多种计息方式与还款计划，动态计算利息。' },
    { id: 10, name: '施工图审查费', description: '自动匹配建筑类别与面积规模。' },
    { id: 11, name: '铺底流动资金', description: '帮助评估项目运营初期的资金占用。' },
    { id: 12, name: '劳动安全性评价费', description: '结合行业规范测算安全评价投入。' },
    { id: 13, name: '海域和无人岛金征费', description: '适用于临海/海岛项目的专项费用。' },
    { id: 14, name: '交通影响评价费', description: '根据交通影响范围与规模快速估算费用。' }
  ];

  const guarantees = [
    { title: '权威数据源', desc: '住建部、发改委、地方造价站等官方文件自动更新。' },
    { title: '透明计算逻辑', desc: '每个工具附计算公式、引用条款，可追溯、可审计。' },
    { title: '一键导出', desc: '支持 PDF / Excel 导出，满足汇报与归档需求。' }
  ];

  return (
    <div className="calculation-library">
      <motion.section
        className="hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.p
            className="eyebrow"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            CALCULATION LIBRARY
          </motion.p>
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            工程费用计算库
          </motion.h1>
          <motion.p
            className="intro"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            基于国家及地方标准打造的在线计算平台，帮助预算、投标、审计团队快速得到可信结果，让成本测算更高效、更透明。
          </motion.p>
          <motion.div
            className="hero-stats"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {heroStats.map((stat, index) => (
              <motion.div
                className="stat-card"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                key={stat.label}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
              >
                <span className="value " style={{ color: '#fff' }}>{stat.value}</span>
                <span className="label">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      <motion.section
        className="tools-list"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="section-header">
            <motion.p
              className="eyebrow"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              TOOLS
            </motion.p>
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              14 种专业工具，覆盖投资决策全过程
            </motion.h2>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              从立项估算、招投标到施工执行，每一步都有标准化工具支撑。
            </motion.p>
          </div>
          <div className="tools-grid">
            {calculationTools.map((tool, index) => (
              <motion.div
                key={tool.id}
                className="tool-card"
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <h3>{tool.name}</h3>
                <p>{tool.description}</p>
                <a href={`http://calc.xingsnb.cn/`} className="primary-button">
                  立即计算
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="guarantees"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="section-header">
            <motion.p
              className="eyebrow"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              WHY TRUST
            </motion.p>
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              准确可信的计算体验
            </motion.h2>
          </div>
          <div className="guarantee-grid">
            {guarantees.map((item, index) => (
              <motion.div
                className="guarantee-card"
                key={item.title}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default CalculationLibrary;
